import { createRoot } from "react-dom/client";
import { createElement } from "react";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import { initTikTokPixel } from "./lib/tiktokPixel";

// Defer TikTok pixel init so a third-party failure (e.g. Safari iOS Private Mode
// blocking 3rd-party storage) can never block React from mounting.
const safeInitTikTokPixel = () => {
  try {
    initTikTokPixel();
  } catch (err) {
    console.warn("[TikTokPixel] init failed", err);
  }
};

if (typeof window !== "undefined") {
  const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(safeInitTikTokPixel);
  } else {
    window.setTimeout(safeInitTikTokPixel, 200);
  }
}

const RECOVERY_KEY = "pittch_boot_recovery_once";

const renderFallback = (root: HTMLElement, message: string) => {
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#FDF8F3;color:#2B3B52;">
      <div style="max-width:420px;text-align:center;">
        <h1 style="font-size:20px;font-weight:600;margin:0 0 12px;">Pittch couldn't start</h1>
        <p style="font-size:15px;line-height:1.5;margin:0 0 20px;color:#52606D;">${message}</p>
        <button id="pittch-reload" style="background:#FC4C03;color:#fff;border:0;border-radius:10px;padding:12px 20px;font-size:15px;font-weight:600;cursor:pointer;">Reload</button>
        <p style="font-size:12px;margin:20px 0 0;color:#7A8794;">If this keeps happening, open Settings → Safari → Advanced → Website Data, find pittch.ai and remove it.</p>
      </div>
    </div>`;
  document.getElementById("pittch-reload")?.addEventListener("click", () => {
    try { sessionStorage.removeItem(RECOVERY_KEY); } catch { /* ignore */ }
    window.location.reload();
  });
};

const tryClearStorage = () => {
  try { window.localStorage.clear(); } catch { /* ignore */ }
  try { window.sessionStorage.clear(); } catch { /* ignore */ }
};

const handleBootFailure = (err: unknown) => {
  console.error("[boot] Fatal error mounting React", err);
  let recoveryAttempted = false;
  try {
    recoveryAttempted = sessionStorage.getItem(RECOVERY_KEY) === "1";
  } catch { /* ignore */ }

  if (!recoveryAttempted) {
    try { sessionStorage.setItem(RECOVERY_KEY, "1"); } catch { /* ignore */ }
    tryClearStorage();
    window.location.reload();
  } else {
    const root = document.getElementById("root");
    if (root) {
      renderFallback(
        root,
        "Your browser is blocking site storage (often Private Browsing or strict cookie settings). Disable Private Browsing for this site or clear site data, then reload."
      );
    }
  }
};

async function boot() {
  try {
    const root = document.getElementById("root")!;
    // Dynamic import so module-evaluation throws (e.g. unguarded localStorage in
    // any deeply imported file) are caught here, not before React can mount.
    const { default: App } = await import("./App.tsx");
    createRoot(root).render(
      createElement(HelmetProvider, null, createElement(App))
    );
  } catch (err) {
    handleBootFailure(err);
  }
}

boot();
